// Stitches S3 (nxmonorepo todos/stitches-aziz-auto.md): with the tenant's product.autoVariants
// flag on, saving the variant attributes creates a row for every option combination, each with a
// prefilled, editable name and a short unique SKU. Rows the user already has are kept as they are.

export const autoVariantsFlag = 'product.autoVariants';

// The API limits a variant SKU to 20 characters (VariantDetailsInputDto).
const maximumSkuLength = 20;

export type VariantAttributeOptions = { name: string; options: string[] };

export type VariantRow = {
  id?: string;
  sku?: string;
  name?: string;
  attributeFields?: Record<string, string>;
  [field: string]: unknown;
};

export function listAttributeCombinations(
  attributes: VariantAttributeOptions[],
): Array<Record<string, string>> {
  return attributes
    .filter((attribute) => attribute.options.length > 0)
    .reduce<Array<Record<string, string>>>(
      (combinations, attribute) =>
        combinations.flatMap((combination) =>
          attribute.options.map((option) => ({ ...combination, [attribute.name]: option })),
        ),
      [{}],
    );
}

// Auto SKUs are ASCII only (A-Z, 0-9 and '-') so Code 128 barcodes (Stitches S8) can encode them.
// Accents are folded (Café -> CAFE); other scripts are dropped.
function skuPart(text: string, length: number): string {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, length);
}

export function generateVariantSku(
  productName: string,
  optionValues: string[],
  takenSkus: Set<string>,
): string {
  const parts = [
    skuPart(productName, 6) || 'ITEM',
    ...optionValues.map((value) => skuPart(value, 3)),
  ];
  const baseSku = parts.filter(Boolean).join('-').slice(0, maximumSkuLength);
  let sku = baseSku;
  for (let copyNumber = 2; takenSkus.has(sku); copyNumber += 1) {
    const suffix = `-${copyNumber}`;
    sku = `${baseSku.slice(0, maximumSkuLength - suffix.length)}${suffix}`;
  }
  return sku;
}

export function buildVariantRows({
  productName,
  attributes,
  existingRows,
}: {
  productName: string;
  attributes: VariantAttributeOptions[];
  existingRows: VariantRow[];
}): VariantRow[] {
  const usedAttributes = attributes.filter((attribute) => attribute.options.length > 0);
  if (usedAttributes.length === 0) {
    return existingRows.length > 0 ? existingRows : [{}];
  }

  const combinationKey = (attributeFields: Record<string, string> | undefined) =>
    JSON.stringify(usedAttributes.map((attribute) => attributeFields?.[attribute.name] ?? null));
  const existingRowByKey = new Map<string, VariantRow>();
  for (const row of existingRows) {
    const key = combinationKey(row.attributeFields);
    if (!existingRowByKey.has(key)) existingRowByKey.set(key, row);
  }

  const takenSkus = new Set(existingRows.map((row) => row.sku).filter(Boolean) as string[]);
  const trimmedProductName = productName.trim();
  const keptRows = new Set<VariantRow>();
  const rows = listAttributeCombinations(usedAttributes).map((combination) => {
    const existingRow = existingRowByKey.get(combinationKey(combination));
    if (existingRow) {
      keptRows.add(existingRow);
      return { ...existingRow, attributeFields: combination };
    }
    const optionValues = usedAttributes.map((attribute) => combination[attribute.name]);
    const sku = generateVariantSku(trimmedProductName, optionValues, takenSkus);
    takenSkus.add(sku);
    return {
      attributeFields: combination,
      name: [trimmedProductName, ...optionValues].filter(Boolean).join('/'),
      sku,
    };
  });

  // A saved variant is never dropped here; deleting it stays a deliberate action on its row.
  const savedRowsOutsideCombinations = existingRows.filter((row) => row.id && !keptRows.has(row));
  return [...rows, ...savedRowsOutsideCombinations];
}
